import React, { useEffect, useState } from "react";
import AllRoutes from "./Routes";
import Header from "./Components/Header";
import styled, { keyframes } from "styled-components";
import { viewSizeCalculator } from "./utils";
import { useDispatch, useSelector } from "react-redux";
import Footer from "./Components/Footer";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  matchPath,
} from "react-router-dom";
import { views } from "./Configs/config";
import { FILTER_KEYS } from "./Configs/filters.config";
import { getItem } from "./utils/localStorage";
import { hasStoredSession } from "./services/apiService";
import { logoutUserApi, restoreSession } from "./Redux/auth/auth.actions";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSnack } from "./Components/SnackBar";

const App = () => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [resetFlag, setResetFlag] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const { loginSuccess } = useSelector((store) => store.auth);
  const dispatch = useDispatch();
  const snack = useSnack();

  // On load, if there is no access token but a previous session existed, try a
  // silent refresh before deciding to redirect to sign-in. After logout the
  // hasSession flag is cleared, so we skip the refresh and the pointless 401.
  useEffect(() => {
    const token = getItem("token");
    if (token) {
      setAuthChecked(true);
    } else if (hasStoredSession()) {
      dispatch(restoreSession()).finally(() => setAuthChecked(true));
    } else {
      setAuthChecked(true);
    }
  }, [dispatch]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Compare screen is just a URL view (`view=compare`); derive the old
  // `isTableVisible` flag from it so every existing consumer keeps working.
  const isCompareView = searchParams.get(FILTER_KEYS.VIEW) === views.COMPARE;
  const state = { isPopupVisible, isTableVisible: isCompareView };

  const toggleState = (key) => {
    if (key === "isTableVisible") {
      // Compare enter -> view=compare; exit -> back to the map. It only renders
      // on "/", so route there first when toggled from another route. A single
      // functional write keeps it from racing other URL updates in the handler.
      const target = isCompareView ? views.MAP : views.COMPARE;
      if (location.pathname !== "/") {
        const next = new URLSearchParams(searchParams);
        next.set(FILTER_KEYS.VIEW, target);
        navigate({ pathname: "/", search: next.toString() });
      } else {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(FILTER_KEYS.VIEW, target);
            return next;
          },
          { replace: true },
        );
      }
      return;
    }
    setIsPopupVisible((prev) => !prev);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  const showHeader = matchPath("/", location.pathname) || matchPath("/hospitals/:id", location.pathname);

  useEffect(() => {
    // Wait until the silent-refresh attempt has resolved so we don't redirect
    // a returning user to sign-in before their session is restored.
    if (!authChecked) return;

    const pathname = location?.pathname;
    const segments = pathname.split("/").filter(Boolean);
    const token = getItem("token");

    const isSignIn = pathname === "/sign-in";
    const isSignUp = pathname === "/sign-up";
    const isRoot = pathname === "/";
    const isExactHospitalDetailPage = segments[0] === "hospitals" && segments.length === 2;

    let shouldHideHeader = false;

    if (isSignIn || isSignUp) {
      shouldHideHeader = true;
    } else if (!token && !isSignIn && !isSignUp) {
      dispatch(logoutUserApi());
      navigate("/sign-in");
    } else if (!isRoot && !isExactHospitalDetailPage) {
      // Hide for all routes except "/" or exact hospital detail page
      shouldHideHeader = true;
    }

    setIsHeaderVisible(!shouldHideHeader);
    }, [location, authChecked]);

  useEffect(() => {
    if (loginSuccess) {
      snack(
        "User successfully signed in",
        "#61C676",
        "#FFFFFF"
      );
    }
  }, [loginSuccess, snack]);

  if (isMobile) {
    return (
      <h1 style={{ textAlign: "center", marginTop: "50px" }}>
        This app is not available on mobile devices.
      </h1>
    );
  }

  return (
    <MainWrapper>
      {showHeader && (
        <Header
          state={state}
          toggleState={toggleState}
          handleClosePopup={handleClosePopup}
          resetFlag={resetFlag}
          setResetFlag={setResetFlag}
          isVisible={isHeaderVisible}
        />
      )}
      <AllRoutes
        toggleState={toggleState}
        state={state}
        resetFlag={resetFlag}
        setResetFlag={setResetFlag}
      />
      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </MainWrapper>
  );
};

export default App;

const MainWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ height }) =>
    height
      ? `calc(100% - ${viewSizeCalculator(height)})`
      : `calc(100% - ${viewSizeCalculator(12.4)})`};
  gap: ${() => `${viewSizeCalculator(0.625)}`}; // 0.625rem

  padding: ${() => `0 ${viewSizeCalculator(1.25)}`}; // 0.625rem
`;

const rotation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const Loader = styled.div`
  width: ${({ width }) => width ? width : `${viewSizeCalculator(1.75)}`};
  height: ${({ height }) => height ? height : `${viewSizeCalculator(1.75)}`};
  border: ${({ border }) => border || `${viewSizeCalculator(0.125)} solid #e35b42`};
  border-bottom-width: ${() => `${viewSizeCalculator(0.125)}`};
  border-bottom-color: ${({ isTransparent }) => isTransparent ? "transparent" : "#f5f5f5"};
  border-radius: 50%;
  display: inline-block;
  animation: ${rotation} 1s linear infinite;
`;
